import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { FileText } from "lucide-react";
import { useMemo } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../ui/tooltip";

import type { Note } from "@cohesive/api-entities";
import {
  BaseEntityCard,
  BaseEntityCardActions,
  BaseEntityCardBody,
  BaseEntityCardContent,
  BaseEntityCardContextMenu,
  BaseEntityCardDescription,
  BaseEntityCardExternalLinkAction,
  BaseEntityCardHeader,
  BaseEntityCardIcon,
  BaseEntityCardMentionAction,
  BaseEntityCardMetadata,
  BaseEntityCardProps,
  BaseEntityCardTitle,
} from "../BaseCard";

export { Note };

type NoteCardProps = Note &
  BaseEntityCardProps & {
    web_link?: string;
  };

// Avatar component for NoteCard
export function NoteCardAvatar({ className, style }: Partial<NoteCardProps>) {
  return (
    <BaseEntityCardIcon className={className} style={style}>
      <FileText className="h-5 w-5 text-muted-foreground" />
    </BaseEntityCardIcon>
  );
}

// Extract text from HTML content
function extractTextFromHtml(html: string): string {
  if (html.includes("<div") || html.includes("<p")) {
    const strippedHtml = html
      .replace(/<[^>]*>/g, " ")
      .replace(/\s{2,}/g, " ")
      .trim();
    return strippedHtml;
  }

  return html;
}

export function NoteCard({
  platform,
  content = "",
  created_at,
  web_link,
  onClick,
  onDoubleClick,
  onMentionClick,
  actions,
  processes,
  className,
  style,
}: NoteCardProps) {
  // Extract plain text from HTML content when needed
  const plainTextContent = useMemo(
    () => extractTextFromHtml(content),
    [content],
  );

  const formattedDate = created_at
    ? formatDistanceToNow(new Date(created_at), { addSuffix: true })
    : "";

  const contentPreview =
    plainTextContent.length > 100
      ? `${plainTextContent.substring(0, 100)}...`
      : plainTextContent;

  return (
    <BaseEntityCard
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      processes={processes}
      className={className}
      style={style}
    >
      <NoteCardAvatar />

      <BaseEntityCardContent>
        <BaseEntityCardHeader>
          <BaseEntityCardTitle platform={platform}>Note</BaseEntityCardTitle>
          <BaseEntityCardActions>
            {onMentionClick && (
              <BaseEntityCardMentionAction onClick={onMentionClick} />
            )}
            {web_link && <BaseEntityCardExternalLinkAction href={web_link} />}
            {actions && actions.length > 0 && (
              <BaseEntityCardContextMenu actions={actions} />
            )}
          </BaseEntityCardActions>
        </BaseEntityCardHeader>

        <BaseEntityCardBody>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <BaseEntityCardDescription
                  className={cn("line-clamp-3 text-foreground")}
                >
                  {contentPreview}
                </BaseEntityCardDescription>
              </TooltipTrigger>
              <TooltipContent className="max-w-md">
                <p
                  className="text-sm"
                  style={{
                    wordBreak: "break-word",
                  }}
                >
                  {plainTextContent}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {formattedDate && (
            <BaseEntityCardMetadata>
              <span>Created {formattedDate}</span>
            </BaseEntityCardMetadata>
          )}
        </BaseEntityCardBody>
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}
